import { useAtomValue } from 'jotai'
import {
	feedAdHasGeneratedContentAtom,
	feedAdMetaAttributionPositionAtom,
	feedAdMetaCardsAtom,
	feedAdMetaGeneralContentFontFamilyOverrideAtom,
	feedAdMetaGeneralContentPaddingAtom,
	feedAdMetaLocationPositionAtom,
	feedAdMetaLogoPositionAtom,
	feedAdMetaStackOrientationAtom,
	feedAdStatusAtom,
} from '../state'
import { ContentAttribution, ContentCta, ContentLocation, ContentLogo, ContentSubtitle, ContentTitle } from './content'

export function FeedAdContentLayer({}: {}) {
	const feedAdMetaLogoPosition = useAtomValue(feedAdMetaLogoPositionAtom)
	const feedAdMetaLocationPosition = useAtomValue(feedAdMetaLocationPositionAtom)
	const feedAdMetaAttributionPosition = useAtomValue(feedAdMetaAttributionPositionAtom)

	const feedAdStatus = useAtomValue(feedAdStatusAtom)
	const hasGeneratedContent = useAtomValue(feedAdHasGeneratedContentAtom)
	const isGeneratingText = feedAdStatus === 'generating' && !hasGeneratedContent
	const shouldFlashContent = (feedAdStatus === 'generating' && hasGeneratedContent)
		|| feedAdStatus === 'tool-executing'

	const generalContentPadding = useAtomValue(feedAdMetaGeneralContentPaddingAtom)
	const fontFamilyOverride = useAtomValue(feedAdMetaGeneralContentFontFamilyOverrideAtom)
	const cardConfiguration = useAtomValue(feedAdMetaCardsAtom)

	const stackOrientation = useAtomValue(feedAdMetaStackOrientationAtom)

	if (generalContentPadding === undefined || !cardConfiguration) {
		return null
	}

	const topLeftZone = (
		<>
			{feedAdMetaLogoPosition === 'top-left' && <ContentLogo />}
			{feedAdMetaLocationPosition === 'top-left' && <ContentLocation isGeneratingText={isGeneratingText} />}
		</>
	)

	const topRightZone = (
		<>
			{feedAdMetaLogoPosition === 'top-right' && <ContentLogo />}
			{feedAdMetaLocationPosition === 'top-right' && <ContentLocation isGeneratingText={isGeneratingText} />}

			{feedAdMetaAttributionPosition === 'top-right' && <ContentAttribution />}
		</>
	)

	const bottomLeftZone = (
		<>
			{feedAdMetaLogoPosition === 'bottom-left' && <ContentLogo />}

			{feedAdMetaLocationPosition === 'bottom-left' && (
				<ContentLocation
					isGeneratingText={isGeneratingText}
				/>
			)}

			<ContentTitle isGeneratingText={isGeneratingText} />

			<ContentSubtitle isGeneratingText={isGeneratingText} />
		</>
	)

	const bottomRightZone = (
		<>
			{feedAdMetaLogoPosition === 'bottom-right' && <ContentLogo />}

			{feedAdMetaLocationPosition === 'bottom-right' && (
				<ContentLocation
					isGeneratingText={isGeneratingText}
				/>
			)}

			{feedAdMetaAttributionPosition === 'bottom-right' && <ContentAttribution />}
		</>
	)

	if (stackOrientation === 'horizontal') {
		return (
			<div
				className={`absolute inset-0 grid grid-cols-[60%_40%] ${shouldFlashContent ? 'animate-pulse' : ''}`}
				style={{
					fontFamily: fontFamilyOverride ?? 'inherit',
				}}
			>
				<div
					className="flex flex-col items-start"
					style={{
						paddingLeft: `${generalContentPadding}px`,
						paddingRight: `${generalContentPadding / 1.5}px`,
						paddingTop: `${generalContentPadding}px`,
						backgroundColor: cardConfiguration.show
							? cardConfiguration.color
							: 'transparent',
					}}
				>
					{/* Top left */}
					<div className="flex items-center flex-col gap-2" data-is="top-left zone">
						{topLeftZone}
					</div>

					<div className="grow-1" />

					{/* Bottom left */}
					<div
						className="relative flex justify-end flex-col gap-2 z-50"
						style={{
							paddingBottom: `${generalContentPadding}px`,
						}}
					>
						{bottomLeftZone}

						<ContentCta isGeneratingText={isGeneratingText} />
					</div>
				</div>

				<div className="flex flex-col items-end">
					{/* Top right */}
					<div
						style={{
							paddingRight: `${generalContentPadding}px`,
							paddingTop: `${generalContentPadding}px`,
						}}
						data-is="top-right zone"
						className="flex flex-col gap-2 items-center"
					>
						{topRightZone}
					</div>

					<div className="grow-1" />

					{/* Bottom right */}
					<div
						className="flex justify-between items-end pt-6"
						style={{
							paddingRight: `${generalContentPadding}px`,
							paddingBottom: `${generalContentPadding}px`,
						}}
					>
						<div data-is="bottom-right zone" className="flex flex-col gap-2 items-end">
							{bottomRightZone}
						</div>
					</div>
				</div>
			</div>
		)
	} else {
		return (
			<div
				className={`absolute inset-0 flex flex-col ${shouldFlashContent ? 'animate-pulse' : ''}`}
				style={{
					paddingTop: `${generalContentPadding}px`,
					fontFamily: fontFamilyOverride ?? 'inherit',
				}}
			>
				<div
					className="flex"
					style={{
						paddingLeft: `${generalContentPadding}px`,
						paddingRight: `${generalContentPadding}px`,
					}}
				>
					<div className="flex items-center flex-col gap-2" data-is="top-left zone">
						{topLeftZone}
					</div>

					{/* Spacer */}
					<div className="grow-1" />

					<div data-is="top-right zone" className="flex flex-col gap-2 items-center">
						{topRightZone}
					</div>
				</div>

				<div className="grow-1" />

				{/* Main area -- the bottom padding isn't part of the global setting */}
				<div
					className="relative flex justify-end flex-col gap-2 z-50"
					style={{
						paddingTop: `${Math.floor(generalContentPadding / 1.5)}px`,
						paddingLeft: `${generalContentPadding}px`,
						paddingRight: `${generalContentPadding}px`,
						backgroundColor: cardConfiguration.show
							? cardConfiguration.color
							: 'transparent',
					}}
				>
					{bottomLeftZone}
				</div>

				<div
					className="flex justify-between items-end pt-6"
					style={{
						paddingLeft: `${generalContentPadding}px`,
						paddingRight: `${generalContentPadding}px`,
						paddingBottom: `${generalContentPadding}px`,
						backgroundColor: cardConfiguration.show
							? cardConfiguration.color
							: 'transparent',
					}}
				>
					<ContentCta isGeneratingText={isGeneratingText} />

					<div data-is="bottom-right zone" className="flex flex-col gap-2 items-end">
						{bottomRightZone}
					</div>
				</div>
			</div>
		)
	}
}
